<div class="main-content">
  <div class="container-fluid">
    <div class="admin-page-header">
      <div>
        <h3>EPE Application</h3>
        <p class="text-muted">Pool of Standardisation Experts — fields marked <span class="req">*</span> are required.</p>
      </div>
    </div>

    <div *ngIf="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <form-error-summary [errors]="fieldErrors" (jumpTo)="jumpToField($event)"></form-error-summary>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- OPEN CALL TOPICS -->
      <div class="form-section" id="field-topics">
        <h4 class="form-section-title">Q1. Select your open call topics <span class="req">*</span></h4>
        <p class="text-muted">Please indicate between one and five open call topics for which you can provide expert advice on standardisation to EU-funded or national projects. The selected topics should reflect demonstrable expertise, current or past standardisation work, and a clear ability to support projects in moving from research outputs towards standardisation pathways.</p>

        <div class="topics-picker" [class.topics-picker--error]="formSubmitted && topics.length < MIN_TOPICS">

          <!-- Selected badges -->
          <div class="topics-selected">
            <span class="topics-count" [class.topics-count--full]="topics.length === MAX_TOPICS">
              {{ topics.length }}/{{ MAX_TOPICS }} selected
            </span>
            <ng-container *ngIf="topics.length > 0; else noTopics">
              <span *ngFor="let t of topics.controls" class="topic-tag">
                <span class="topic-tag-group">{{ t.get('groupLabel')?.value }}</span>
                {{ t.get('label')?.value }}
                <span class="topic-tag-remove" (click)="removeTopic(t.get('value')?.value)" title="Remove">&#x2715;</span>
              </span>
            </ng-container>
            <ng-template #noTopics>
              <span class="topics-empty">No topics selected yet — expand a category below.</span>
            </ng-template>
          </div>

          <!-- Tree -->
          <div class="topics-tree">
            <div *ngFor="let group of TOPIC_GROUPS" class="tg">
              <div class="tg-header" (click)="toggleGroup(group.label)">
                <i class="fa fa-fw" [ngClass]="isGroupExpanded(group.label) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                <strong>{{ group.label }}</strong>
              </div>
              <div *ngIf="isGroupExpanded(group.label)" class="tg-body">
                <div *ngFor="let sub of group.subGroups" class="tg-sub">
                  <div class="tg-sub-header">
                    <span class="tg-expand" (click)="toggleSubGroup(sub.value)">
                      <i class="fa fa-fw" [ngClass]="isSubGroupExpanded(sub.value) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </span>
                    <span class="tg-item"
                          [class.tg-item--selected]="isTopicSelected(sub.value)"
                          [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(sub.value)"
                          (click)="selectTopic(sub.value, sub.label, group.label)">
                      {{ sub.label }}
                    </span>
                  </div>
                  <div *ngIf="isSubGroupExpanded(sub.value)" class="tg-children">
                    <div *ngFor="let child of sub.children" class="tg-child">
                      <span class="tg-item tg-item--child"
                            [class.tg-item--selected]="isTopicSelected(child.value)"
                            [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(child.value)"
                            (click)="selectTopic(child.value, child.label, group.label)">
                        {{ child.label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div *ngIf="formSubmitted && topics.length < MIN_TOPICS" class="field-error">
          Please select between {{ MIN_TOPICS }} and {{ MAX_TOPICS }} topics.
        </div>

        <!-- Per-topic detail cards -->
        <div class="topic-details" formArrayName="topics" *ngIf="topics.length > 0">
          <div *ngFor="let topicCtrl of topics.controls; let i = index"
               [formGroupName]="i" class="panel panel-default entry-panel topic-detail-card">
            <div class="panel-heading clearfix">
              <strong>{{ topicCtrl.get('groupLabel')?.value }} — {{ topicCtrl.get('label')?.value }}</strong>
              <button type="button" class="btn btn-xs btn-danger pull-right" (click)="removeTopic(topicCtrl.get('value')?.value)">
                <i class="fa fa-trash"></i> Remove
              </button>
            </div>
            <div class="panel-body">
              <div class="form-group" [id]="'field-topic-' + i + '-evidence'">
                <label>Evidence of expertise <span class="req">*</span></label>
                <textarea class="form-control" rows="4" maxlength="1000"
                          [class.input-error]="formSubmitted && topicCtrl.get('evidence_of_expertise')?.invalid"
                          formControlName="evidence_of_expertise"
                          placeholder="Summarise relevant standards, deliverables, technical committees, policy references, publications, projects or advisory experience."></textarea>
                <small class="char-counter">{{ (topicCtrl.get('evidence_of_expertise')?.value || '').length }}/1000</small>
                <div *ngIf="formSubmitted && topicCtrl.get('evidence_of_expertise')?.invalid" class="field-error">
                  Evidence of expertise is required (max 1000 characters).
                </div>
              </div>
              <div class="form-group" [id]="'field-topic-' + i + '-support'">
                <label>Primary type of support offered <span class="req">*</span></label>
                <div class="support-options">
                  <label class="support-option" *ngFor="let opt of SUPPORT_TYPE_OPTIONS">
                    <input type="checkbox"
                           [checked]="isSupportSelected(topicCtrl, opt)"
                           (change)="toggleSupportType(topicCtrl, opt)">
                    {{ opt }}
                  </label>
                </div>
                <div *ngIf="formSubmitted && topicCtrl.get('primary_support_types')?.invalid" class="field-error">
                  Select at least one type of support.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STANDARDISATION EXPERIENCE -->
      <div class="form-section">
        <h4 class="form-section-title">Q2. Standardisation roles and evidence of participation</h4>
        <div class="form-group">
          <label>Do you have any experience or roles in standardisation?</label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="standardisation_experience" value="no"> No
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="standardisation_experience" value="yes"> Yes
            </label>
          </div>
        </div>

        <div *ngIf="form.get('standardisation_experience')?.value === 'yes'" class="form-group">
          <label>Are you a member of an SDO?</label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="sdo_member" value="no"> No
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="sdo_member" value="yes"> Yes
            </label>
          </div>
        </div>

        <div id="field-standardisation_roles" *ngIf="form.get('standardisation_experience')?.value === 'yes' && form.get('sdo_member')?.value === 'yes'" formArrayName="standardisation_roles">
          <p class="text-muted small">
            Indicate the Standards Development Organisation (SDO) / National Standards Body (NSB) / National Committee (NC).
            <span class="req">*</span> At least one role is required.
          </p>
          <div *ngFor="let roleGroup of standardisationRoles.controls; let i = index"
               [formGroupName]="i" class="panel panel-default entry-panel">
            <div class="panel-heading clearfix">
              <strong>Role {{ i + 1 }}</strong>
              <button type="button" class="btn btn-xs btn-danger pull-right" (click)="removeStandardisationRole(i)">
                <i class="fa fa-trash"></i> Remove
              </button>
            </div>
            <div class="panel-body">
              <div class="form-group">
                <label>Select the Standards development organisation, national standards body or national committee you are involved in</label>
                <select class="form-control" formControlName="sdo">
                  <option value="">— Select —</option>
                  <optgroup *ngFor="let grp of SDO_OPTIONS" [label]="grp.group">
                    <option *ngFor="let opt of grp.options" [value]="opt">{{ opt }}</option>
                  </optgroup>
                </select>
              </div>
              <div *ngIf="isOtherSdo(roleGroup)" class="form-group">
                <label>Please specify</label>
                <input type="text" class="form-control" formControlName="other_sdo">
              </div>
              <p class="text-muted small">
                Use full code where available, for example ISO/IEC JTC 1/SC 27. Information security, cybersecurity and privacy protection
              </p>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Number of TC/SC/WG</label>
                    <input type="text" class="form-control" formControlName="tc_number" placeholder="e.g. ISO/IEC 27001">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Title of TC/SC/WG</label>
                    <input type="text" class="form-control" formControlName="tc_title" placeholder="e.g. Information security management">
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label>What is your role(s) in the Technical Committee, Sub-Committee or Working Group</label>
                    <select class="form-control" formControlName="roles">
                      <option value="">— Select —</option>
                      <option *ngFor="let r of ROLE_OPTIONS" [value]="r">{{ r }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Years of experience in the TC, SC or WG</label>
                    <select class="form-control" formControlName="years_of_experience">
                      <option value="">— Select —</option>
                      <option *ngFor="let y of YEARS_OPTIONS" [value]="y">{{ y }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button type="button" class="btn btn-default btn-sm" (click)="addStandardisationRole()">
            <i class="fa fa-plus"></i> Add Role
          </button>
          <div *ngIf="formSubmitted && standardisationRoles.length === 0" class="field-error">
            Please add at least one standardisation role.
          </div>
        </div>
      </div>

      <!-- Q3 — EXPLAIN YOUR FIT -->
      <div class="form-section">
        <h4 class="form-section-title">Q3. Explain your fit with HSbooster.eu II services</h4>

        <div class="form-group" id="field-fit_support_approach">
          <label>Standardisation support approach <span class="req">*</span></label>
          <textarea class="form-control" rows="5" maxlength="2000"
                    [class.input-error]="formSubmitted && form.get('fit_support_approach')?.invalid"
                    formControlName="fit_support_approach"></textarea>
          <p class="text-muted small">Explain how you would support R&I projects to identify standardisation potential, select relevant SDOs or committees, and translate project results into actionable standardisation activities.</p>
          <small class="char-counter">{{ (form.get('fit_support_approach')?.value || '').length }}/2000</small>
          <div *ngIf="formSubmitted && form.get('fit_support_approach')?.invalid" class="field-error">
            This field is required (max 2000 characters).
          </div>
        </div>

        <div class="form-group" id="field-fit_ri_experience">
          <label>Experience with supporting research and innovation projects</label>
          <textarea class="form-control" rows="4" maxlength="1500"
                    [class.input-error]="formSubmitted && form.get('fit_ri_experience')?.invalid"
                    formControlName="fit_ri_experience"></textarea>
          <p class="text-muted small">Describe previous work and support provided to Horizon Europe, Horizon 2020, Digital Europe, LIFE, Interreg, national research programmes or comparable initiatives.</p>
          <small class="char-counter">{{ (form.get('fit_ri_experience')?.value || '').length }}/1500</small>
          <div *ngIf="formSubmitted && form.get('fit_ri_experience')?.invalid" class="field-error">
            Maximum 1500 characters.
          </div>
        </div>

        <div class="form-group" id="field-fit_knowledge_valorisation">
          <label>Evidence of knowledge valorisation experience</label>
          <textarea class="form-control" rows="4" maxlength="1000"
                    [class.input-error]="formSubmitted && form.get('fit_knowledge_valorisation')?.invalid"
                    formControlName="fit_knowledge_valorisation"></textarea>
          <p class="text-muted small">Mention examples where standards, technical specifications, guidelines or pre-normative outputs helped increase the uptake of research results.</p>
          <small class="char-counter">{{ (form.get('fit_knowledge_valorisation')?.value || '').length }}/1000</small>
          <div *ngIf="formSubmitted && form.get('fit_knowledge_valorisation')?.invalid" class="field-error">
            Maximum 1000 characters.
          </div>
        </div>

        <div class="form-group" id="field-fit_additional_expertise">
          <label>Additional standardisation expertise outside of those available in the list of topics</label>
          <textarea class="form-control" rows="4" maxlength="1000"
                    [class.input-error]="formSubmitted && form.get('fit_additional_expertise')?.invalid"
                    formControlName="fit_additional_expertise"></textarea>
          <p class="text-muted small">Use this field only for expertise relevant to R&I projects but not explicitly covered by the list of topics.</p>
          <small class="char-counter">{{ (form.get('fit_additional_expertise')?.value || '').length }}/1000</small>
          <div *ngIf="formSubmitted && form.get('fit_additional_expertise')?.invalid" class="field-error">
            Maximum 1000 characters.
          </div>
        </div>
      </div>

      <!-- Q4 — AVAILABILITY, LANGUAGES AND PREFERENCES -->
      <div class="form-section">
        <h4 class="form-section-title">Q4. Availability, languages and preferences</h4>

        <div class="form-group" id="field-availability">
          <label>Indicative availability for HSbooster.eu II mentoring assignments <span class="req">*</span></label>
          <select class="form-control"
                  [class.input-error]="formSubmitted && form.get('availability')?.invalid"
                  formControlName="availability">
            <option value="">— Select —</option>
            <option *ngFor="let a of AVAILABILITY_OPTIONS" [value]="a">{{ a }}</option>
          </select>
          <div *ngIf="formSubmitted && form.get('availability')?.invalid" class="field-error">
            This field is required.
          </div>
        </div>

        <div class="form-group" id="field-preferred_services">
          <label>Preferred types of services <span class="req">*</span></label>
          <div class="support-options">
            <label class="support-option" *ngFor="let s of SERVICE_OPTIONS">
              <input type="checkbox"
                     [checked]="isChoiceSelected('preferred_services', s)"
                     (change)="toggleChoice('preferred_services', s)">
              {{ s }}
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('preferred_services')?.invalid" class="field-error">
            Select at least one service.
          </div>
        </div>

        <div class="form-group" id="field-working_languages">
          <label>Working languages <span class="req">*</span></label>
          <p class="text-muted small">Indicate all languages in which you can provide professional standardisation support. English is required.</p>
          <div class="support-options">
            <label class="support-option" *ngFor="let l of LANGUAGE_OPTIONS">
              <input type="checkbox"
                     [checked]="isChoiceSelected('working_languages', l)"
                     [disabled]="l === 'English'"
                     (change)="toggleChoice('working_languages', l)">
              {{ l }}
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('working_languages')?.invalid" class="field-error">
            Select at least one language.
          </div>
        </div>

        <div class="form-group" id="field-time_zone">
          <label>Time zone <span class="req">*</span></label>
          <input type="text" class="form-control"
                 [class.input-error]="formSubmitted && form.get('time_zone')?.invalid"
                 formControlName="time_zone">
          <p class="text-muted small">Use standard time zone format, for example CET/CEST.</p>
          <div *ngIf="formSubmitted && form.get('time_zone')?.invalid" class="field-error">
            This field is required.
          </div>
        </div>

        <div class="form-group">
          <label>Potential limitations on availability</label>
          <textarea class="form-control" rows="3"
                    formControlName="availability_limitations"></textarea>
          <p class="text-muted small">Mention constraints that could affect your availability.</p>
        </div>
      </div>

      <!-- Q5 — UPLOAD CV AND DECLARATIONS -->
      <div class="form-section">
        <h4 class="form-section-title">Q5. Upload CV and declarations</h4>

        <div class="form-group" id="field-cv">
          <label>CV <span class="req">*</span></label>
          <div class="cv-upload-area" [class.cv-upload-area--error]="formSubmitted && form.get('cv')?.invalid">
            <input type="file" accept="application/pdf" #cvInput style="display:none"
                   (change)="onCvFileChange($event)">
            <button type="button" class="btn btn-default btn-sm"
                    (click)="cvInput.click()" [disabled]="cvUploading">
              <i class="fa" [ngClass]="cvUploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
              {{ cvFilename ? 'Replace PDF' : 'Upload PDF' }}
            </button>
            <span *ngIf="cvFilename" class="cv-status cv-status--ok">
              <i class="fa fa-file-pdf-o"></i> {{ cvFilename }}
            </span>
            <span *ngIf="cvUploading" class="cv-status">Uploading...</span>
          </div>
          <p class="text-muted small">English version only. The CV should include standardisation roles and selected topic expertise.</p>
          <div *ngIf="cvUploadError" class="field-error">{{ cvUploadError }}</div>
          <div *ngIf="formSubmitted && form.get('cv')?.invalid" class="field-error">
            Please upload your CV before submitting.
          </div>
        </div>

        <div class="form-group" id="field-confidentiality_declaration">
          <label class="checkbox-inline">
            <input type="checkbox" formControlName="confidentiality_declaration">
            I agree to treat non-public project information as confidential. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('confidentiality_declaration')?.invalid" class="field-error">
            This declaration is required.
          </div>
        </div>

        <div class="form-group" id="field-accuracy_declaration">
          <label class="checkbox-inline">
            <input type="checkbox" formControlName="accuracy_declaration">
            I confirm that the information provided is accurate and can be verified. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('accuracy_declaration')?.invalid" class="field-error">
            This declaration is required.
          </div>
        </div>

        <div class="form-group" id="field-data_protection_consent">
          <label class="checkbox-inline">
            <input type="checkbox" formControlName="data_protection_consent">
            I consent to the processing of my application data for the assessment, management and delivery of HSbooster.eu II expert services. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('data_protection_consent')?.invalid" class="field-error">
            This consent is required.
          </div>
        </div>

        <div class="form-group" id="field-public_profile_consent">
          <label>I consent to the publication of selected professional profile information on HSbooster.eu II channels. <span class="req">*</span></label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="public_profile_consent" value="yes"> Yes
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="public_profile_consent" value="no"> No
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('public_profile_consent')?.invalid" class="field-error">
            Please select an option.
          </div>
        </div>

        <div class="form-group" id="field-newsletter_subscription">
          <label>I consent to subscribe to the HSbooster.eu II newsletter. <span class="req">*</span></label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="newsletter_subscription" value="yes"> Yes
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="newsletter_subscription" value="no"> No
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('newsletter_subscription')?.invalid" class="field-error">
            Please select an option.
          </div>
        </div>
      </div>

      <button type="submit" class="btn btn-primary btn-fill" [disabled]="submitting">
        <span *ngIf="submitting"><i class="fa fa-spinner fa-spin"></i> Submitting...</span>
        <span *ngIf="!submitting">Submit Application</span>
      </button>
      <button type="button" class="btn btn-default" style="margin-left: 10px;" [disabled]="savingDraft" (click)="saveDraft()">
        <span *ngIf="savingDraft"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
        <span *ngIf="!savingDraft"><i class="fa fa-floppy-o"></i> Save as Draft</span>
      </button>
      <span *ngIf="draftSaved" class="text-success" style="margin-left: 10px; font-size: 13px;">
        <i class="fa fa-check"></i> Draft saved
      </span>
      <a routerLink="/my-applications" class="form-cancel-link">Cancel</a>

    </form>
  </div>
</div>
